import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { useSenders } from '@/features/senders/hooks/useSenders';
import {
  CreateConfigurationDto,
  SenderConfiguration,
} from '@/features/senders/types/sender.types';
import {
  useCreateSenderConfiguration,
  useUpdateSenderConfiguration,
} from '@/hooks/useSenderConfigurationMutations';
import { getPriorityLabel } from '@/utils/sender-config';
import { Info, Mail, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface SenderConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  domainUuid?: string;
  folderUuid?: string;
  currentConfig?: SenderConfiguration;
  onSave: () => void;
}

export const SenderConfigModal = ({
  isOpen,
  onClose,
  domainUuid,
  folderUuid,
  currentConfig,
  onSave,
}: SenderConfigModalProps) => {
  const { t } = useTranslation();
  const { data: senders = [], isLoading: isLoadingSenders } = useSenders();
  const createConfigMutation = useCreateSenderConfiguration();
  const updateConfigMutation = useUpdateSenderConfiguration();

  const [selectedSenderUuid, setSelectedSenderUuid] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Determine priority based on context
  const priority = folderUuid ? 2 : domainUuid ? 1 : 0;
  const contextType = folderUuid ? 'folder' : domainUuid ? 'domain' : 'default';

  // Set initial selected sender when modal opens
  useEffect(() => {
    if (isOpen && currentConfig) {
      setSelectedSenderUuid(currentConfig.senderUuid);
    } else if (isOpen) {
      setSelectedSenderUuid('');
    }
  }, [isOpen, currentConfig]);

  const handleSave = async () => {
    if (!selectedSenderUuid) return;

    setIsSubmitting(true);
    try {
      const configData: CreateConfigurationDto = {
        senderUuid: selectedSenderUuid,
        domainUuid,
        folderUuid,
        priority,
      };

      if (currentConfig) {
        await updateConfigMutation.mutateAsync({
          uuid: currentConfig.uuid,
          data: configData,
        });
      } else {
        await createConfigMutation.mutateAsync(configData);
      }

      onSave();
      onClose();
    } catch (error) {
      console.error('Failed to save sender configuration:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedSender = senders.find(
    sender => sender.uuid === selectedSenderUuid
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      title={t('senderConfig.modal.title', "Configuration de l'expéditeur")}
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-primary-100 flex h-10 w-10 items-center justify-center rounded-xl">
              <Mail className="text-primary-600 h-5 w-5" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-neutral-900">
                {t('senderConfig.modal.title', "Configuration de l'expéditeur")}
              </h2>
              <p className="text-sm text-neutral-600">
                {t(
                  'senderConfig.modal.description',
                  "Choisissez l'expéditeur email pour ce {{type}}",
                  {
                    type: t(`senderConfig.priority.${contextType}`, {
                      default: 'Défaut',
                      domain: 'Domaine',
                      folder: 'Dossier',
                    }),
                  }
                )}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Priority Info */}
        <div className="border-primary-200 bg-primary-50 rounded-xl border p-4">
          <div className="flex items-start gap-3">
            <Info className="text-primary-600 mt-0.5 h-5 w-5 flex-shrink-0" />
            <div className="space-y-2">
              <h3 className="text-primary-900 font-semibold">
                {t('senderConfig.hierarchy.title', 'Hiérarchie des priorités')}
              </h3>
              <p className="text-primary-800 text-sm">
                {t(
                  'senderConfig.hierarchy.description',
                  "L'expéditeur le plus spécifique sera utilisé en priorité"
                )}
              </p>
              <div className="text-primary-700 space-y-1 text-sm">
                <div className="flex items-center gap-2">
                  <span className="bg-success-100 text-success-800 inline-flex h-5 w-5 items-center justify-center rounded-full text-xs font-bold">
                    2
                  </span>
                  {t(
                    'senderConfig.hierarchy.folder',
                    'Dossier (priorité 2) - Le plus spécifique'
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="bg-primary-100 text-primary-800 inline-flex h-5 w-5 items-center justify-center rounded-full text-xs font-bold">
                    1
                  </span>
                  {t(
                    'senderConfig.hierarchy.domain',
                    'Domaine (priorité 1) - Spécifique au domaine'
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-neutral-100 text-xs font-bold text-neutral-800">
                    0
                  </span>
                  {t(
                    'senderConfig.hierarchy.default',
                    'Défaut (priorité 0) - Expéditeur par défaut'
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sender Selection */}
        <div className="space-y-3">
          <label className="block text-sm font-medium text-neutral-900">
            {t('senderConfig.modal.selectSender', 'Sélectionner un expéditeur')}
          </label>

          {isLoadingSenders ? (
            <div className="space-y-2">
              {[...Array(3)].map((_, i) => (
                <div
                  key={i}
                  className="h-12 animate-pulse rounded-lg bg-neutral-200"
                />
              ))}
            </div>
          ) : (
            <div className="max-h-64 space-y-2 overflow-y-auto">
              {senders.map(sender => (
                <button
                  key={sender.uuid}
                  onClick={() => setSelectedSenderUuid(sender.uuid)}
                  className={`w-full rounded-lg border p-3 text-left transition-colors ${selectedSenderUuid === sender.uuid
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50'
                    }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="bg-primary-50 flex h-10 w-10 items-center justify-center rounded-xl">
                        <Mail className="text-primary-600 h-5 w-5" />
                      </div>
                      <div>
                        <p className="font-medium text-neutral-900">
                          {sender.name}
                        </p>
                        <p className="text-sm text-neutral-600">
                          {sender.email}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {sender.isDefault && (
                        <span className="rounded-full bg-neutral-100 px-2 py-1 text-xs font-medium text-neutral-800">
                          {t('senders.status.default', 'Défaut')}
                        </span>
                      )}
                      {!sender.isActive && (
                        <span className="inline-flex items-center rounded-full border border-neutral-200 bg-neutral-100 px-2 pt-1 text-xs font-medium text-neutral-800">
                          {t('senders.status.inactive', 'Inactif')}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Selected Sender Info */}
        {selectedSender && (
          <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4">
            <h4 className="mb-2 font-medium text-neutral-900">
              {t('senderConfig.modal.selectedSender', 'Expéditeur sélectionné')}
            </h4>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-neutral-600">
                  {t('common.name', 'Nom')}
                </span>
                <span className="font-medium text-neutral-900">
                  {selectedSender.name}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-neutral-600">
                  {t('common.email', 'Email')}
                </span>
                <span className="font-medium text-neutral-900">
                  {selectedSender.email}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-neutral-600">
                  {t('senderConfig.modal.priority', 'Priorité')}
                </span>
                <span className="font-medium text-neutral-900">
                  {getPriorityLabel(priority)}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            {t('senderConfig.modal.cancel', 'Annuler')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={!selectedSenderUuid || isSubmitting}
          >
            {isSubmitting
              ? t('common.saving', 'Enregistrement...')
              : t('senderConfig.modal.save', 'Enregistrer')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
